import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import useWebSocket from '../hooks/useWebSocket';

const WebSocketContext = createContext(null);

const DEFAULT_VALUE = {
  status: 'idle',
  lastMessage: null,
  unreadMessages: 0,
  resetUnreadMessages: () => {},
};

function buildWsUrl(token) {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  // http:// → ws://, https:// → wss://
  const wsBase = base.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws?token=${encodeURIComponent(token)}`;
}

/**
 * One WebSocket connection per signed-in user, shared across the whole app.
 * Tracks a live unread-message counter for the sidebar badge.
 */
export function WebSocketProvider({ children }) {
  const { token, user } = useAuth();
  const enabled = Boolean(token && user);
  const url = enabled ? buildWsUrl(token) : null;

  const { status, lastMessage } = useWebSocket(url, { enabled });
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (lastMessage?.type === 'new_message') {
      setUnreadMessages((n) => n + 1);
    }
  }, [lastMessage]);

  const value = useMemo(
    () => ({
      status,
      lastMessage,
      unreadMessages,
      resetUnreadMessages: () => setUnreadMessages(0),
    }),
    [status, lastMessage, unreadMessages],
  );

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

/**
 * Safe to call outside a provider — returns inert defaults so components and
 * their isolated unit tests don't need to be wrapped.
 */
export function useWebSocketContext() {
  return useContext(WebSocketContext) || DEFAULT_VALUE;
}

export default WebSocketContext;
